import React from 'react';
import Image from 'next/image';
import imageUrlBuilder from '@sanity/image-url';
import { PortableText, PortableTextComponents } from '@portabletext/react';
import { merge, cloneDeep } from 'lodash-es';
import { getImageDimensions } from '@sanity/asset-utils';
import { prodClient } from './lib/client';
import { sanityImageUrl } from '@/lib/utils';
import Link from 'next/link';
// import arrowSvg from '@/public/global/svg/arrow.svg';

const sanityImageBuilder = imageUrlBuilder(prodClient);

export const richTextComponent: PortableTextComponents = {
  marks: {
    serif: ({ children }) => (
      <span className="font-instrument-serif font-[400] text-[calc(1em+0.5rem)]">{children}</span>
    ),
    underline: ({ children }) => <u className="underline">{children}</u>,
    em: ({ children }) => <em className="pr-[.125em]">{children}</em>,
    inlineImage: ({ value }) => {
      const url = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : '';
      const dimensions = value?.image?.asset?._ref
        ? getImageDimensions(value?.image?.asset?._ref)
        : {
            width: 0,
            height: 0,
            aspectRatio: 0,
          };
      return (
        <span className="w-[3rem] h-auto inline-block mx-4">
          <Image
            className="object-contain w-full h-full"
            src={{
              src: url,
              width: dimensions.width,
              height: dimensions.height,
            }}
            alt="Icon image"
            quality={100}
          />
        </span>
      );
    },
    inlineCornerChip: ({ value, children }) => {
      const imageUrl = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : null;
      const text = value?.text || '';

      return (
        <span className="rt-inlineCornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <span className="leading-none text-black font-geistmono text-[2rem] translate-y-[.125rem]">
            {text}
          </span>
        </span>
      );
    },
    link: ({ value, children }) => {
      const href = value?.href || '';
      const target = value?.blank ? '_blank' : undefined;
      const rel = value?.blank ? 'noopener noreferrer' : undefined;

      return (
        <Link href={href} target={target} rel={rel} className="underline">
          {children}
        </Link>
      );
    },
  },
  block: {
    h1: ({ children }) => <h1 className="">{children}</h1>,
    h2: ({ children }) => <h2 className="">{children}</h2>,
    h3: ({ children }) => <h3 className="">{children}</h3>,
    h4: ({ children }) => <h4 className="">{children}</h4>,
    h5: ({ children }) => <h5 className="">{children}</h5>,
    h6: ({ children }) => <h6 className="">{children}</h6>,
    small: ({ children }) => (
      <p className="font-inter-tight text-[1.75rem] leading-[120%]">{children}</p>
    ),
    normal: ({ children }) => (
      <p className="font-inter-tight text-[2rem] lg:text-[2.25rem] font-[400] leading-[150%] lg:leading-[140%]">{children}</p>
    ),
    medium: ({ children }) => (
      <p className="font-inter-tight leading-[140%] text-[2rem] lg:text-[2.5rem]">
        {children}
      </p>
    ),
  },
  types: {
    image: ({ value }) => {
      return (
        <Image
          className="rounded-[1.25rem] my-[2rem]"
          src={{
            src: sanityImageBuilder.image(value).url(),
            width: getImageDimensions(value)?.width,
            height: getImageDimensions(value)?.height,
          }}
          alt={value?.alt || ''}
        />
      );
    },
    imageDesktop: ({ value }) => {
      const imageAsset = value?.asset;
      if (!imageAsset) return null;
      return (
        <Image
          className="hidden w-full lg:block rounded-[1.25rem]"
          src={{
            src: sanityImageBuilder.image(imageAsset).url(),
            width: getImageDimensions(imageAsset)?.width,
            height: getImageDimensions(imageAsset)?.height,
          }}
          alt={value?.alt || ''}
        />
      );
    },
    imageMobile: ({ value }) => {
      const imageAsset = value?.asset;
      if (!imageAsset) return null;
      return (
        <Image
          className="lg:hidden rounded-[1.25rem]"
          src={{
            src: sanityImageBuilder.image(imageAsset).url(),
            width: getImageDimensions(imageAsset)?.width,
            height: getImageDimensions(imageAsset)?.height,
          }}
          alt={value?.alt || ''}
        />
      );
    },
    solidLine: ({ value }) => {
      const marginClass = value?.marginSize || 'my-[4rem]';

      return (
        <hr
          className={`w-full border-t border-white border-opacity-20 ${marginClass}`}
        />
      );
    },
    dashedLine: ({ value }) => {
      const marginClass = value?.marginSize || 'my-[3rem] lg:my-[4rem]';

      return (
        <hr
          className={`w-full border-0 h-[1px] lg:h-[2px] ${marginClass}`}
          style={{
            backgroundImage:
              'repeating-linear-gradient(to right, #10303733 0, #10303733 .75rem, transparent .75rem, transparent 1.5rem)',
          }}
        />
      );
    },
    cornerList: ({ value }) => {
      return (
        <ul className="flex flex-col items-start p-0 list-none cornerList mb-[1.25em] gap-[1rem]">
          {value?.items?.map((item: any, index: number) => {
            return (
              <li key={index} className="flex items-center p-0 m-0 gap-[1rem]">
                <div className="cornerListCorner bg-[#112628] flex-shrink-0 w-[1.5rem] h-[1.5rem] [clip-path:polygon(0_0,calc(100%-.75rem)_0,100%_.75rem,100%_100%,0_100%)]" />
                <div className="richtext richtext-start prose-p:leading-[1.1em]">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      );
    },
    cornerChip: ({ value }) => {
      const imageUrl = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : null;
      const text = value?.text || '';

      return (
        <div className="rt-cornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] prose-p:text-black bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <p className="mb-0 leading-none font-geistmono text-[2rem] translate-y-[.125rem]">
            {text}
          </p>
        </div>
      );
    },
    numberedList: ({ value }) => {
      const items = value?.items || [];
      const startNumber = value?.startNumber || 1;

      return (
        <ol
          className="flex flex-col items-start p-0 list-none numberedList mb-[1.25rem] gap-[1rem]"
          start={startNumber}
        >
          {items.map((item: any, index: number) => {
            return (
              <li
                key={index}
                className="flex items-center p-0 m-0 gap-[.625rem]"
              >
                <span className="flex-shrink-0 text-[#D9F9B9] font-geist min-w-[2rem]">
                  {startNumber + index}.
                </span>

                <div className="richtext richtext-start">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      );
    },
    iconList: ({ value }) => {
      const items = value?.items || [];

      return (
        <div className="flex flex-col gap-[2rem] lg:gap-[3rem]">
          {items.map((item: any, index: number) => {
            const iconUrl = item?.icon?.asset?._ref
              ? sanityImageUrl(item?.icon?.asset?._ref)
              : null;
            const title = item?.title || '';

            return (
              <div key={index} className="flex flex-col iconItem gap-[3rem] lg:gap-[1rem]">
                <div className="flex flex-row items-center gap-[1rem]">
                  {iconUrl && (
                    <Image
                      className="!w-[3rem] !h-[3rem] !m-0 aspect-square flex-shrink-0"
                      src={iconUrl}
                      width={32}
                      height={32}
                      quality={100}
                      alt=""
                    />
                  )}
                  {title && (
                    <span className="text-fakeblack/[64%] font-inter-tight font-[400] text-[2.25rem]">{title}</span>
                  )}
                </div>
                {item?.text && (
                  <div className="flex-1">
                    <div className="richtext richtext-start prose-p:text-[2rem] prose-p:leading-[120%] prose-strong:font-[600] text-center lg:text-left">
                      <PortableText
                        value={item?.text}
                        components={richTextComponent}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    },
    iconItem: ({ value }) => {
      const iconUrl = value?.icon?.asset?._ref
        ? sanityImageUrl(value?.icon?.asset?._ref)
        : null;
      const title = value?.title || '';

      return (
        <div className="flex flex-col items-start iconItem gap-[1rem] mb-[1rem]">
          <div className="flex items-center gap-[2.25rem]">
            {iconUrl && (
              <Image
                className="!w-[5rem] !h-[5rem] !m-0 flex-shrink-0"
                src={iconUrl}
                width={32}
                height={32}
                alt=""
              />
            )}
            {title && (
              <p className="m-0 text-[#1C1F22] text-[2.5rem]">{title}</p>
            )}
          </div>
          <div className="flex-1">
            <div className="richtext richtext-start prose-p:text-[2.25rem] prose-strong:font-[600]">
              <PortableText
                value={value?.text}
                components={richTextComponent}
              />
            </div>
          </div>
        </div>
      );
    },
    arrowBullet: ({ value }) => {
      const imageUrl = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : null;

      return (
        <div className="flex items-start arrowBullet gap-[1rem] mb-[1rem]">
          {imageUrl && (
            <Image
              className="!w-[3.25rem] !h-[3.25rem] !m-0 flex-shrink-0"
              src={imageUrl}
              width={32}
              height={32}
              alt=""
            />
          )}
          <div className="flex-1 richtext richtext-start prose-p:text-[2.25rem] prose-strong:font-[600]">
            <PortableText value={value?.text} components={richTextComponent} />
          </div>
        </div>
      );
    },
    table: ({ value }) => {
      const headers = value?.headers || [];
      const rows = value?.rows || [];

      return (
        <div className="my-[3rem] lg:my-[4rem] overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {headers.map((header: any, index: number) => (
                  <th
                    key={index}
                    className="border border-white/20 bg-secondary px-[1.5rem] py-[1rem] text-left font-inter-tight text-[1.5rem] lg:text-[2rem] text-white font-semibold"
                  >
                    {header.text}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any, rowIndex: number) => (
                <tr key={rowIndex}>
                  {row.cells?.map((cell: any, cellIndex: number) => (
                    <td
                      key={cellIndex}
                      className="border border-white/20 px-[1rem] lg:px-[1.5rem] py-[1rem] text-[1.5rem] lg:text-[2rem] align-top"
                    >
                      <div className="richtext richtext-start prose-p:text-[1.5rem] lg:prose-p:text-[2rem] font-inter-tight prose-p:mb-[0.5rem] prose-p:last:mb-0">
                        <PortableText
                          value={cell.content}
                          components={richTextComponent}
                        />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    },
  },
  list: {
    bullet: ({ children }) => (
      <ul className="my-4 space-y-4 text-[#103037]/70">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="my-4 space-y-4 text-[#103037]/70">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="flex flex-row gap-[1.5rem] items-start leading-[3.5rem]">
        {/* <Image
          src={arrowSvg}
          alt="arrow"
          className="flex-shrink-0 my-0 mr-[1.5rem] w-[2.35313rem]"
          width={27}
          height={27}
        /> */}
        <span className="block mt-[1.5rem] w-[0.75rem] min-w-[0.75rem] min-h-[0.75rem] h-[0.75rem] rounded-full bg-secondary"></span>
        <span className="block font-inter-tight text-[2.5rem]">{children}</span>
      </li>
    ),
    number: ({ children, index }) => (
      <li className="flex items-start leading-[3rem]">
        <span className="flex flex-shrink-0 justify-center items-center leading-none text-white text-[1.75rem] px-[0.75rem] py-[1rem] bg-[#103037] rounded-[0.66rem] mr-[1.5rem] min-w-[3.25rem]">
          {String((index ?? 0) + 1).padStart(2, '0')}
        </span>
        <span>{children}</span>
      </li>
    ),
  },
};

export const customRichTextComponent = (props: PortableTextComponents) => {
  return merge(cloneDeep(richTextComponent), props);
};
